import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const PUBLIC_PATH_PREFIXES = ['/camisetas', '/biblioteca'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isPublic = PUBLIC_PATH_PREFIXES.some(p =>
        window.location.pathname === p || window.location.pathname.startsWith(p + '/')
      );
      if (!isPublic) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const createCRUDService = (endpoint) => ({
  getAll: (params, customPath = '') => api.get(`${endpoint}${customPath}`, { params }),
  getById: (id) => api.get(`${endpoint}/${id}`),
  create: (data) => api.post(endpoint, data),
  update: (id, data) => api.put(`${endpoint}/${id}`, data),
  delete: (id) => api.delete(`${endpoint}/${id}`),
  getRivalDetails: (id) => api.get(`${endpoint}/${id}/rival-details`),
  getDTRivalDetails: (id) => api.get(`${endpoint}/${id}/dt-rival-details`),
  getJugadorPeriodo: (id) => api.get(`${endpoint}/${id}/jugador-periodo`),
  getDTPeriodo: (id) => api.get(`${endpoint}/${id}/dt-periodo`),
  getJuezPeriodo: (id) => api.get(`${endpoint}/${id}/juez-periodo`),
  getJugadoresSinAtilio: () => api.get(`${endpoint}/jugadores-sin-atilio`),
  getDetalle: (id) => api.get(`${endpoint}/${id}/detalle`),
  combinar: (id, destinoId) => api.post(`${endpoint}/${id}/combinar`, { destinoId }),
  getPartidosJugador: (id) => api.get(`${endpoint}/${id}/partidos-jugador`),
  getPartidosDT: (id) => api.get(`${endpoint}/${id}/partidos-dt`),
  getPartidosRival: (id) => api.get(`${endpoint}/${id}/partidos-rival`),
  getPartidosDTRival: (id) => api.get(`${endpoint}/${id}/partidos-dt-rival`),
  getPartidosJuez: (id) => api.get(`${endpoint}/${id}/partidos-juez`)
});

export const services = {
  paises: createCRUDService('/paises'),
  departamentos: createCRUDService('/departamentos'),
  ciudades: createCRUDService('/ciudades'),
  paisesAnteriores: createCRUDService('/paises-anteriores'),
  equipos: createCRUDService('/equipos'),
  equiposHistorico: createCRUDService('/equipos-historico'),
  estadios: createCRUDService('/estadios'),
  estadiosHistorico: createCRUDService('/estadios-historico'),
  motivosPartido: createCRUDService('/motivos-partido'),
  posiciones: createCRUDService('/posiciones'),
  nombresCampeonato: createCRUDService('/nombres-campeonato'),
  campeonatos: createCRUDService('/campeonatos'),
  tablasPosiciones: createCRUDService('/tablas-posiciones'),
  tablasPosicionesEquipo: createCRUDService('/tablas-posiciones-equipo'),
  personas: createCRUDService('/personas'),
  jugadores: createCRUDService('/jugadores'),
  jugadoresTrayectoria: createCRUDService('/jugadores-trayectoria'),
  directoresTecnicos: createCRUDService('/directores-tecnicos'),
  jueces: createCRUDService('/jueces'),
  partidos: createCRUDService('/partidos'),
  partidosGoles: createCRUDService('/partidos-goles'),
  partidosGolesRival: createCRUDService('/partidos-goles-rival'),
  partidosJugadores: createCRUDService('/partidos-jugadores'),
  partidosRivales: createCRUDService('/partidos-rivales'),
  partidosDT: createCRUDService('/partidos-dt'),
  partidosRivalesDT: createCRUDService('/partidos-rivales-dt'),
  partidosPenales: createCRUDService('/partidos-penales'),
  partidosJueces: createCRUDService('/partidos-jueces'),
  recursos: createCRUDService('/recursos'),
  recursosEntidad: createCRUDService('/recursos-entidad'),
  parametros: createCRUDService('/parametros'),
  camisetas: {
    ...createCRUDService('/camisetas'),
    getStats: (id) => api.get(`/camisetas/${id}/stats`),
    getSponsors: (id) => api.get(`/camisetas/${id}/sponsors`),
    addSponsor: (id, data) => api.post(`/camisetas/${id}/sponsors`, data),
    updateSponsor: (id, csId, data) => api.put(`/camisetas/${id}/sponsors/${csId}`, data),
    deleteSponsor: (id, csId) => api.delete(`/camisetas/${id}/sponsors/${csId}`),
  },
  marcas: createCRUDService('/marcas'),
  sponsors: createCRUDService('/sponsors'),
  camisetasAsignacion: {
    bulkAssign: (data) => api.post('/partidos/bulk-camiseta', data),
  },
  camisetasSponsors: {
    getExisting: (params) => api.get('/camisetas-sponsors/existing', { params }),
    bulkAssign: (data) => api.post('/camisetas-sponsors/bulk', data),
  },
  jugadoresRivales: {
    getAll: (params) => api.get('/jugadores-rivales/con-stats', { params }),
    getStats: (id) => api.get(`/jugadores-rivales/${id}/stats`),
  },
  dtsRivales: {
    getAll: (params) => api.get('/dts-rivales/con-stats', { params }),
    getInfo: (id) => api.get(`/dts-rivales/${id}/info`),
    getPartidos: (id) => api.get(`/dts-rivales/${id}/partidos`),
  },
  publicaciones: {
    ...createCRUDService('/publicaciones'),
    getNumeros: (id) => api.get(`/publicaciones/${id}/numeros`),
  },
  publicacionNumeros: createCRUDService('/publicacion-numeros')
};

export const campeonatoSubService = {
  getPartidos: (id) => api.get(`/campeonatos/${id}/partidos`),
  getJugadores: (id) => api.get(`/campeonatos/${id}/jugadores`),
  getTablaPosiciones: (id) => api.get(`/campeonatos/${id}/tabla-posiciones`),
  saveTablaPosiciones: (id, rows) => api.post(`/campeonatos/${id}/tabla-posiciones`, { rows }),
  getEquiposCampeonato: (id) => api.get(`/campeonatos/${id}/equipos-campeonato`),
  updateTablaRow: (tablaId, pos, data) => api.put(`/tablas-posiciones-equipo/${tablaId}/${pos}`, data),
};

export const partidoResumenService = {
  getResumen: (filters) => api.get('/partidos/resumen', { params: filters })
};

export const partidoAtilioService = {
  getSinAtilio: () => api.get('/partidos/sin-atilio'),
  procesarAtilio: (updates) => api.post('/partidos/procesar-atilio', { updates })
};

export const campeonatoAdicionalService = {
  asignar: (campeonatoAdicionalId, partidoIds) =>
    api.post('/partidos/asignar-campeonato-adicional', { campeonatoAdicionalId, partidoIds })
};

export const mantenimientoService = {
  getPartidosRecursos: (params) => api.get('/mantenimiento/partidos-recursos', { params }),
  updateRecursosUrl: (slot, items) => api.put('/mantenimiento/recursos-url', { slot, items }),
};

export const partidoSubService = {
  getGoles: (id) => api.get(`/partidos/${id}/goles`),
  createGol: (id, data) => api.post(`/partidos/${id}/goles`, data),
  updateGol: (id, golId, data) => api.put(`/partidos/${id}/goles/${golId}`, data),
  deleteGol: (id, golId) => api.delete(`/partidos/${id}/goles/${golId}`),

  getGolesRival: (id) => api.get(`/partidos/${id}/goles-rival`),
  createGolRival: (id, data) => api.post(`/partidos/${id}/goles-rival`, data),
  updateGolRival: (id, golId, data) => api.put(`/partidos/${id}/goles-rival/${golId}`, data),
  deleteGolRival: (id, golId) => api.delete(`/partidos/${id}/goles-rival/${golId}`),

  getJugadores: (id) => api.get(`/partidos/${id}/jugadores`),
  createJugador: (id, data) => api.post(`/partidos/${id}/jugadores`, data),
  updateJugador: (id, jugadorId, data) => api.put(`/partidos/${id}/jugadores/${jugadorId}`, data),
  deleteJugador: (id, jugadorId) => api.delete(`/partidos/${id}/jugadores/${jugadorId}`),

  getRivales: (id) => api.get(`/partidos/${id}/rivales`),
  createRival: (id, data) => api.post(`/partidos/${id}/rivales`, data),
  updateRival: (id, rivalPersonaId, data) => api.put(`/partidos/${id}/rivales/${rivalPersonaId}`, data),
  deleteRival: (id, rivalPersonaId) => api.delete(`/partidos/${id}/rivales/${rivalPersonaId}`),

  getDTs: (id) => api.get(`/partidos/${id}/dts`),
  createDT: (id, data) => api.post(`/partidos/${id}/dts`, data),
  updateDT: (id, dtId, data) => api.put(`/partidos/${id}/dts/${dtId}`, data),
  deleteDT: (id, dtId) => api.delete(`/partidos/${id}/dts/${dtId}`),

  getRivalesDT: (id) => api.get(`/partidos/${id}/rivales-dt`),
  createRivalDT: (id, data) => api.post(`/partidos/${id}/rivales-dt`, data),
  updateRivalDT: (id, rivalDTId, data) => api.put(`/partidos/${id}/rivales-dt/${rivalDTId}`, data),
  deleteRivalDT: (id, rivalDTId) => api.delete(`/partidos/${id}/rivales-dt/${rivalDTId}`),

  getJueces: (id) => api.get(`/partidos/${id}/jueces`),
  createJuez: (id, data) => api.post(`/partidos/${id}/jueces`, data),
  updateJuez: (id, juezId, data) => api.put(`/partidos/${id}/jueces/${juezId}`, data),
  deleteJuez: (id, juezId) => api.delete(`/partidos/${id}/jueces/${juezId}`),

  getPenales: (id) => api.get(`/partidos/${id}/penales`),
  createPenal: (id, data) => api.post(`/partidos/${id}/penales`, data),
  updatePenal: (id, penalId, data) => api.put(`/partidos/${id}/penales/${penalId}`, data),
  deletePenal: (id, penalId) => api.delete(`/partidos/${id}/penales/${penalId}`),

  getJuecesConPersona: () => api.get('/jueces-con-persona'),
  getJugadoresConPersona: () => api.get('/jugadores-con-persona'),
  getDTsConPersona: () => api.get('/dts-con-persona'),
  getPersonasHistorialRival: () => api.get('/personas-historial-rival'),
  getPartidosConJuez: (anio) => api.get('/partidos-con-juez', { params: { anio } }),
  setJuezPrincipal: (id, data) => api.put(`/partidos/${id}/juez-principal`, data),

  getRecursos: (id) => api.get(`/partidos/${id}/recursos`),
  createRecurso: (id, data) => api.post(`/partidos/${id}/recursos`, data),
  uploadRecursoFoto: (id, file) => api.post(`/partidos/${id}/recursos/upload-foto`, file, {
    headers: { 'Content-Type': 'application/octet-stream' },
    transformRequest: [(d) => d],
    timeout: 120000,
  }),
  updateRecurso: (id, recursoId, data) => api.put(`/partidos/${id}/recursos/${recursoId}`, data),
  deleteRecurso: (id, recursoId) => api.delete(`/partidos/${id}/recursos/${recursoId}`),

  // Reasignación masiva
  reasignarRival: (data) => api.post('/partidos/reasignar-rival', data),
  reasignarRivalDT: (data) => api.post('/partidos/reasignar-rival-dt', data),
  reasignarJuez: (data) => api.post('/partidos/reasignar-juez', data),
};

export default api;
